import apiFetch from '@wordpress/api-fetch';
import { useRef, useState } from '@wordpress/element';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-solid.svg';
import { ReactComponent as EyeSlashIcon } from '../../assets/icons/eye-slash-solid.svg';

import './RegisterFromPhoneForm.scss';

export default function RegisterFromPhoneForm({ atts }) {
    const searchParams = new URLSearchParams(window.location.search);
    const [ formStatus, setFormStatus ] = useState('idle'); // idle, loading.
    const [ currentFormStep, setCurrentFormStep ] = useState(`${atts.start_step}-lookup-prompt`);
    const [ showPasswordValue, setShowPasswordValue ] = useState(false);
    const [ phoneNumber, setPhoneNumber ] = useState(searchParams.get('phone')?.replace('+1', '') || '');
    const [ otp, setOtp ] = useState('');
    const [ firstName, setFirstName ] = useState('');
    const [ lastName, setLastName ] = useState('');
    const [ email, setEmail ] = useState(searchParams.get('email')?.replace('+1', '') || '');
    const [ password, setPassword ] = useState('');
    const [ contactId, setContactId ] = useState(null);
    const otpKeyField = useRef(atts.start_step);

    const sendOtp = async ( method = 'sms' ) => {

        if ( 'loading' === formStatus ) {
            window.console.warn('Prevented duplicate form submission during loading');
            return;
        }

        setFormStatus('loading');

        otpKeyField.current = ( ['sms', 'call'].includes(method) ? 'phone' : 'email' );
        const encodedFieldValue = (
            'phone' === otpKeyField.current ?
            // Treat `+` as literal entities rather than space entity.
            '%2B1' + encodeURIComponent(phoneNumber.replaceAll(/[^0-9]+/g,'')) :
            encodeURIComponent(email)
        );

        apiFetch({
            path: `/ptf-twilio/v1/send-otp?to=${encodedFieldValue}&method=${method}`,
            method: 'GET',
        }).then(res => {
            if ( 'success' !== res?.status ) {
                throw res?.message || 'An unknown error prevented OTP from being sent';
            }
            setCurrentFormStep('enter-otp');
        }).catch(error => {
            window.console.error(error);
            window.alert(error?.message || error);
        }).finally(() => {
            setFormStatus('idle');
        });
    }

    const handlePhoneLookupSubmit = async (submitEvent) => {
        submitEvent.preventDefault();

        if ( 'loading' === formStatus ) {
            window.console.warn('Prevented duplicate form submission during loading');
            return;
        }

        sendOtp('sms');
    }

    const handleEmailLookupSubmit = async (submitEvent) => {
        submitEvent.preventDefault();

        if ( 'loading' === formStatus ) {
            window.console.warn('Prevented duplicate form submission during loading');
            return;
        }

        sendOtp('email');
    }

    const handleOTPSubmit = async (submitEvent) => {
        submitEvent.preventDefault();

        if ( 'loading' === formStatus ) {
            window.console.warn('Prevented duplicate form submission during loading');
            return;
        }

        setFormStatus('loading');

        const encodedFieldValue = (
            'phone' === otpKeyField.current ?
            // Treat `+` as literal entities rather than space entity.
            '%2B1' + encodeURIComponent(phoneNumber.replaceAll(/[^0-9]+/g,'')) :
            encodeURIComponent(email)
        );

        apiFetch({
            path: `/ptf-twilio/v1/keap/find_contacts_by_${otpKeyField.current}?${otpKeyField.current}=${encodedFieldValue}&otp=${encodeURIComponent(otp)}`,
            method: 'GET',
        }).then(res => {
            const contactExists = ( !! res?.data?.contacts?.[0]?.Id );
            if ( contactExists ) {
                const contact = res.data.contacts[0];
                setFirstName(contact?.FirstName || '');
                setLastName(contact?.LastName || '');
                setEmail(contact?.Email || email || '');
                setPhoneNumber(contact?.Phone1 || phoneNumber || '');
                setContactId(Number.parseInt(contact.Id));
            }
            if ( res?.data?.contacts?.[0]?.__wp_user_ids?.length > 0 ) {
                setCurrentFormStep('user-has-account');
            } else {
                if ( ! contactExists ) {
                    setOtp(''); // Erase OTP value for new input.
                    setCurrentFormStep(`contact-not-found-by-${otpKeyField.current}`);
                } else {
                    // Let existing contact register as a website user.
                    setCurrentFormStep('user-register-form');
                }
            }
        }).catch(error => {
            window.console.error(error);
            if ( error?.message ) {
                window.alert(error?.message);
            }
        }).finally(() => {
            setFormStatus('idle');
        });
    }

    const handleRegisterSubmit = (submitEvent) => {
        submitEvent.preventDefault();

        if ( 'loading' === formStatus ) {
            window.console.log('Prevented duplicate form submission during loading');
            return;
        }

        setFormStatus('loading');

        apiFetch({
            path: '/ptf-twilio/v1/keap/register_user',
            method: 'POST',
            data: {
                "contact_id": contactId,
                "first_name": firstName,
                "last_name": lastName,
                "email": email,
                "password": password,
                "phone": `+1${phoneNumber}`,
                "otp": otp,
                "autologin": atts?.autologin || 'false',
            }
        }).then(res => {
            if ( 'success' === res?.status ) {
                if ( atts?.success_url ) {
                    window.location.href = atts.success_url;
                } else {
                    window.location.reload();
                }
            } else {
                window.console.error(res);
            }
        }).catch(error => {
            window.console.error(error);
            if ( error?.message ) {
                window.alert(error?.message);
            }
        }).finally(() => {
            setFormStatus('idle');
        });
    }

    const handleStartPhoneLookupClick = (clickEvent) => {
        clickEvent.preventDefault();
        setPhoneNumber('');
        setCurrentFormStep('phone-lookup-prompt');
    }

    const handleStartEmailLookupClick = (clickEvent) => {
        clickEvent.preventDefault();
        setEmail('');
        setCurrentFormStep('email-lookup-prompt');
    }

    const submitButtonPrefixIcon = (
        ( 'loading' === formStatus ) ? <i class="fa fa-circle-notch fa-spin"></i> : null
    );

    let innerContent = null;
    switch ( currentFormStep ) {
        case 'phone-lookup-prompt':
            innerContent = (
                <form onSubmit={handlePhoneLookupSubmit}>
                    <div>
                        <div>
                            <label>Phone</label>
                            <div className='input-with-prefix'>
                                <span>+1</span>
                                <input
                                    type='tel'
                                    value={phoneNumber}
                                    onInput={event => { setPhoneNumber(event.target.value); }}
                                    required
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <button type='submit' disabled={( 'loading' === formStatus )}>{submitButtonPrefixIcon}Continue</button>
                            <p>By clicking "Continue" above, you consent to receive a one-time verification code to the provided phone number. Message and data rates may apply.</p>
                        </div>
                    </div>
                </form>
            );
            break;

        case 'email-lookup-prompt':
            innerContent = (
                <form onSubmit={handleEmailLookupSubmit}>
                    <div>
                        <div>
                            <label>Email</label>
                            <input
                                type='email'
                                value={email}
                                onInput={event => { setEmail(event.target.value); }}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <button type='submit' disabled={( 'loading' === formStatus )}>{submitButtonPrefixIcon}Continue</button>
                            <p>By clicking "Continue" above, you consent to receive a one-time verification code to the provided email address.</p>
                        </div>
                    </div>
                </form>
            );
            break;

        case 'enter-otp':
            let formTitleText = null;
            let didNotReceive = null;
            let goBackButton = null;
            if ( 'email' === otpKeyField.current ) {
                formTitleText = `Verification code sent to ${email}`;
                didNotReceive = <p><a href='#' onClick={clickEvent => { clickEvent.preventDefault(); sendOtp('email'); }}>Resend email</a>.</p>;
                goBackButton = <a href='#' onClick={handleStartEmailLookupClick}>&larr; Go Back</a>;
            } else if ( 'phone' === otpKeyField.current ) {
                formTitleText = `Verification code sent to ${phoneNumber}`;
                didNotReceive = <p><a href='#' onClick={clickEvent => { clickEvent.preventDefault(); sendOtp('sms'); }}>Resend</a> or <a href='#' onClick={clickEvent => { clickEvent.preventDefault(); sendOtp('call'); }}>call instead</a>.</p>;
                goBackButton = <a href='#' onClick={handleStartPhoneLookupClick}>&larr; Go Back</a>;
            }
            innerContent = (
                <form onSubmit={handleOTPSubmit}>
                    <h2>{formTitleText}</h2>
                    <div>
                        <div>
                            <label>Please enter your 6-digit verification code</label>
                            <input
                                type='text'
                                inputmode='numeric'
                                pattern='[0-9]{6}'
                                value={otp}
                                onInput={event => { setOtp(event.target.value); }}
                                required
                            />
                        </div>
                    </div>
                    <p>Didn't receive the code?</p>
                    {didNotReceive}
                    <div>
                        <div>
                            {goBackButton}
                        </div>
                        <div>
                            <button type='submit' disabled={( 'loading' === formStatus )}>{submitButtonPrefixIcon}Continue</button>
                        </div>
                    </div>
                </form>
            );
            break;

        case 'user-register-form':
            innerContent = (
                <form onSubmit={handleRegisterSubmit}>
                    <div>
                        <div>
                            <label>First Name</label>
                            <input
                                type='text'
                                value={firstName}
                                onInput={event => { setFirstName(event.target.value); }}
                                required
                            />
                        </div>
                        <div>
                            <label>Last Name</label>
                            <input
                                type='text'
                                value={lastName}
                                onInput={event => { setLastName(event.target.value); }}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <label>Phone</label>
                            <input
                                type='tel'
                                value={phoneNumber}
                                required
                                readOnly
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <label>Email</label>
                            <input
                                type='email'
                                value={email}
                                onInput={event => { setEmail(event.target.value); }}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <label>Create Password</label>
                            <div className='input-with-button'>
                                <input
                                    type={showPasswordValue ? 'text' : 'password'}
                                    value={password}
                                    onInput={event => { setPassword(event.target.value); }}
                                    required
                                />
                                <button
                                    type='button'
                                    title={showPasswordValue ? 'Hide password' : 'Show password'}
                                    onClick={() => { setShowPasswordValue( ! showPasswordValue ); }}
                                >
                                    { showPasswordValue ? <EyeIcon /> : <EyeSlashIcon /> }
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <button type='submit' disabled={( 'loading' === formStatus )}>{submitButtonPrefixIcon}Register</button>
                        </div>
                    </div>
                </form>
            );
            break;

        case 'user-has-account':
            innerContent = (
                <div>
                    <h2>Welcome back!</h2>
                    <p>You seem to already have an account with us!</p>
                    <p>Please sign in or contact support for assistance.</p>
                </div>
            );
            break;

        case 'contact-not-found-by-phone':
            innerContent = (
                <div>
                    <h2>{`We did not find an account with phone number ${phoneNumber}`}</h2>
                    <ul>
                        <li>
                            <a href='#' onClick={handleStartPhoneLookupClick}>Try a different phone number</a>
                        </li>
                        <li>
                            <a href='#' onClick={handleStartEmailLookupClick}>Try looking up by email</a>
                        </li>
                        {
                            atts?.account_create_url && (
                                <li>
                                    <a href={atts.account_create_url}>Create a new account</a>
                                </li>
                            )
                        }
                        {
                            atts?.contact_support_url && (
                                <li>
                                    <a href={atts.contact_support_url}>Contact Support</a>
                                </li>
                            )
                        }
                    </ul>
                </div>
            );
            break;

        case 'contact-not-found-by-email':
            innerContent = (
                <div>
                    <h2>{`We did not find an account with email ${email}`}</h2>
                    <ul>
                        <li>
                            <a href='#' onClick={handleStartEmailLookupClick}>Try a different email</a>
                        </li>
                        <li>
                            <a href='#' onClick={handleStartPhoneLookupClick}>Try looking up by phone number</a>
                        </li>
                        {
                            atts?.account_create_url && (
                                <li>
                                    <a href={atts.account_create_url}>Create a new account</a>
                                </li>
                            )
                        }
                        {
                            atts?.contact_support_url && (
                                <li>
                                    <a href={atts.contact_support_url}>Contact Support</a>
                                </li>
                            )
                        }
                    </ul>
                </div>
            );
            break;

        default:
            innerContent = (
                <div>
                    <h2 style={{ color: 'red' }}>{`ERROR: ${currentFormStep} is not a valid form step`}</h2>
                    {
                        ( ! ['phone', 'email'].includes(atts?.start_step) ) &&
                        <p>{`start_step="${atts?.start_step}" is invalid. Must be "email" or "phone".`}</p>
                    }
                </div>
            );
            break;
    }

    return (
        <div className="ptf-twilio-RegisterFromPhoneForm">
            {innerContent}
        </div>
    );
}
